import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PositionType } from 'data/types';
import { addDrawnSpaceToLevel } from 'features/structure';
import { setActiveItem } from 'features/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getItem } from 'utils/data';
import {
  ADD_SPACE_RESETS_DESIGNS_MESSAGE,
  getDrawnSpaceErrorMessage
} from 'utils/drawn-space-messages';
import {
  collectUserPlacedSpacePolygons,
  resolveDrawnSpaceShape
} from 'utils/drawn-space-shape';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { isSpaceDrawingActive, SpaceDrawMode } from 'utils/space-draw-mode';
import { structureEditResetsDesigns } from 'utils/structure-edit-reset';
import { confirmationModal, createActiveItem } from 'utils/ui';
import { useNewSpaceGate } from './use-new-space-gate';
import {
  PendingDrawnSpace,
  UseSpaceDrawingParams,
  UseSpaceDrawingResult
} from './use-space-drawing-types';

export function useSpaceDrawing({
  activeLevel,
  isAddSpacesStep
}: UseSpaceDrawingParams): UseSpaceDrawingResult {
  const dispatch = useAppDispatch();
  const { autoLayoutSpaceIds } = useAppSelector((state) => state.structure);
  const { configurations } = useAppSelector((state) => state.configurations);
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const [spaceDrawMode, setSpaceDrawMode] = useState<SpaceDrawMode>('select');
  const [pendingDrawnSpace, setPendingDrawnSpace] =
    useState<PendingDrawnSpace | null>(null);
  const [isTypePickerOpen, setIsTypePickerOpen] = useState(false);
  const { isBlocked: isNewSpaceBlocked } = useNewSpaceGate();

  const activeLevelId = activeLevel?.uniqueId;
  const isDrawingSpace = isSpaceDrawingActive(isAddSpacesStep, spaceDrawMode);

  const otherSpacePolygons = useMemo(
    () =>
      activeLevel
        ? collectUserPlacedSpacePolygons(activeLevel, autoLayoutSpaceIds)
        : [],
    [activeLevel, autoLayoutSpaceIds]
  );

  useEffect(() => {
    setSpaceDrawMode('select');
    setPendingDrawnSpace(null);
    setIsTypePickerOpen(false);
  }, [isAddSpacesStep, activeLevelId]);

  useEffect(() => {
    if (isNewSpaceBlocked) {
      setSpaceDrawMode('select');
    }
  }, [isNewSpaceBlocked]);

  useEffect(() => {
    if (!isDrawingSpace || isTypePickerOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSpaceDrawMode('select');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawingSpace, isTypePickerOpen]);

  const handleSpaceStrokeComplete = useCallback(
    (rawPoints: PositionType[]) => {
      if (!activeLevel) return;
      const result = resolveDrawnSpaceShape(
        rawPoints,
        getEffectiveFootprintPolygons(activeLevel),
        otherSpacePolygons
      );
      if (result.status !== 'resolved') {
        toast.error(getDrawnSpaceErrorMessage(result.status));
        return;
      }
      setPendingDrawnSpace({
        vertices: result.vertices,
        areaSqft: result.areaSqft
      });
      setIsTypePickerOpen(true);
    },
    [activeLevel, otherSpacePolygons]
  );

  const commitDrawnSpace = useCallback(
    async (levelId: string, spaceItemId: number, vertices: PositionType[]) => {
      const newSpace = await dispatch(
        addDrawnSpaceToLevel({ levelId, spaceItemId, vertices })
      ).unwrap();
      if (!newSpace || getItem(newSpace.itemId)?.code !== 'custom') return;
      const activeItem = createActiveItem({ type: 'space', item: newSpace });
      if (activeItem) {
        dispatch(setActiveItem(activeItem));
      }
    },
    [dispatch]
  );

  const handleDrawnSpaceTypeSelect = useCallback(
    (itemId: number) => {
      const pending = pendingDrawnSpace;
      setPendingDrawnSpace(null);
      setIsTypePickerOpen(false);
      if (!pending || !activeLevelId) return;

      const commit = () => {
        commitDrawnSpace(activeLevelId, itemId, pending.vertices);
      };
      if (structureEditResetsDesigns(configurations, selectedSystems)) {
        confirmationModal(ADD_SPACE_RESETS_DESIGNS_MESSAGE, commit);
      } else {
        commit();
      }
    },
    [
      activeLevelId,
      commitDrawnSpace,
      configurations,
      pendingDrawnSpace,
      selectedSystems
    ]
  );

  const handleCancelDrawnSpace = useCallback(() => {
    setPendingDrawnSpace(null);
    setIsTypePickerOpen(false);
  }, []);

  return {
    spaceDrawMode,
    setSpaceDrawMode,
    isDrawingSpace,
    pendingDrawnSpace,
    typePickerOpenState: [isTypePickerOpen, setIsTypePickerOpen],
    handleSpaceStrokeComplete,
    handleDrawnSpaceTypeSelect,
    handleCancelDrawnSpace
  };
}
